import React, { useState } from "react";
import axios from "axios";
import { useParams, Link as RouterLink } from "react-router-dom";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import {
  Card,
  Link,
  Container,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import Page from "../components/Page";
import Logo from "../components/Logo";
import useResponsive from "../hooks/useResponsive";
import { useTheme } from "@emotion/react";

// PASSWORD VALIDATION SCHEMA
const resetSchema = yup.object().shape({
  newPassword: yup
    .string()
    .required("New password is required.")
    .min(8, "Minimum 8 characters.")
    .max(20, "Maximum 20 characters.")
    .matches(/^[A-Z]/, "Must start with a capital letter.")
    .matches(/[!@#$%^&*(),.?":{}|<>]/, "Must contain one special character."),

  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword"), null], "Passwords do not match.")
    .required("Please confirm password."),
});

// ---- Styles ----
const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("md")]: { display: "flex" },
}));

const HeaderStyle = styled("header")(({ theme }) => ({
  top: 0,
  zIndex: 9,
  width: "100%",
  display: "flex",
  alignItems: "center",
  position: "absolute",
  padding: theme.spacing(3),
  justifyContent: "space-between",
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(7, 5, 0, 7),
  },
}));

const SectionStyle = styled(Card)(({ theme }) => ({
  maxWidth: 464,
  width: "100%",
  margin: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
}));

const ContentStyle = styled("div")(({ theme }) => ({
  maxWidth: 480,
  margin: "auto",
  padding: theme.spacing(12, 0),
  minHeight: "100vh",
}));

export default function ResetPassword() {
  const theme = useTheme();
  const smUp = useResponsive("up", "sm");
  const mdUp = useResponsive("up", "md");

  const { token } = useParams();
  const [serverMsg, setServerMsg] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(resetSchema),
  });

  const onSubmit = async (data) => {
    setServerMsg("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_LIVE_URL}/adminPanel/resetPassword`,
        {
          token,
          newPassword: data.newPassword,
        }
      );

      setServerMsg(response.data.msg);

      if (response.data.status) {
        setTimeout(() => (window.location.href = "/login"), 1500);
      }
    } catch (err) {
      setServerMsg(err.response?.data?.msg || "Server error.");
    }
  };

  return (
    <Page title="Reset Password">
      <RootStyle>
        
        {/* HEADER SAME AS LOGIN PAGE */}
        <HeaderStyle>
          <Logo sx={{ width: "150px", height: "80px", marginLeft: "65%" }} />

          {smUp && (
            <Typography>
              Back to{" "}
              <Link variant="subtitle2" component={RouterLink} to="/login">
                Login
              </Link>
            </Typography>
          )}
        </HeaderStyle>

        {/* LEFT IMAGE SECTION */}
        {mdUp && (
          <SectionStyle>
            <Typography variant="h3" sx={{ px: 5, mt: 18 }}>
              Set New Password
            </Typography>
            <img src="/static/illustrations/illustration_login.png" alt="reset" />
          </SectionStyle>
        )}

        {/* RIGHT CONTENT SECTION */}
        <Container maxWidth="sm">
          <ContentStyle>
            <Typography variant="h4" gutterBottom>
              Create{" "}
              <span style={{ color: theme.palette.primary.mainTheme }}>
                New Password
              </span>
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              {/* NEW PASSWORD */}
              <Typography sx={{ mb: 1 }}>New Password</Typography>
              <TextField
                fullWidth
                type={showNew ? "text" : "password"}
                placeholder="Enter new password"
                {...register("newPassword")}
                error={!!errors.newPassword}
                helperText={errors.newPassword?.message}
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNew(!showNew)}>
                        {showNew ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* CONFIRM PASSWORD */}
              <Typography sx={{ mb: 1 }}>Confirm Password</Typography>
              <TextField
                fullWidth
                type={showConfirm ? "text" : "password"}
                placeholder="Re-enter password"
                {...register("confirmPassword")}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirm(!showConfirm)}>
                        {showConfirm ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  py: 1.5,
                  background: "linear-gradient(90deg, #9a2eff, #8b24f4)",
                }}
              >
                {isSubmitting ? "Resetting..." : "Reset Password"}
              </Button>
            </form>

            {serverMsg && (
              <Typography sx={{ mt: 3, fontWeight: 600 }}>{serverMsg}</Typography>
            )}
          </ContentStyle>
        </Container>
      </RootStyle>
    </Page>
  );
}
